import { Plugin, PluginKey } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { createFileLogger } from '../../../utils/logger';

const log = createFileLogger('CITATION_CLEANUP');

const citationCleanupKey = new PluginKey('citationCleanup');

/**
 * Plugin that cleans up orphaned citation data from Y.js Map when citations are deleted
 * 
 * Citations can be deleted by:
 * - Selecting the citation text and pressing delete/backspace
 * - Using Cmd+A to select all citation text then deleting
 * - Clicking to place cursor, then pressing Cmd+Shift+Left/Right to select, then deleting
 */
export function citationCleanupPlugin(ydoc: any) {
  return new Plugin({
    key: citationCleanupKey,
    
    view(view: EditorView) {
      // Initial scan
      scanAndCleanup(view, ydoc);
      
      return {
        update(view: EditorView, prevState: any) {
          // Check if document changed
          if (view.state.doc !== prevState.doc) {
            scanAndCleanup(view, ydoc);
          }
        }
      };
    }
  });
}

/**
 * Scan document for citations and clean up orphaned data from Y.js Map
 */
function scanAndCleanup(view: EditorView, ydoc: any) {
  if (!ydoc) return;
  
  try {
    // Collect current citation IDs in document
    const currentCitationIds = new Set<string>();
    view.state.doc.descendants((node: any) => {
      if (node.marks) {
        node.marks.forEach((mark: any) => {
          if (mark.type.name === 'citation') {
            currentCitationIds.add(mark.attrs.citationId);
          }
        });
      }
    });
    
    // Get Y.js citations map
    const citationsMap = ydoc.getMap('citations');
    const allStoredIds = Array.from(citationsMap.keys()) as string[];
    
    // Find orphaned citations (in Y.js but not in document)
    const orphanedIds = allStoredIds.filter((id: string) => !currentCitationIds.has(id));
    
    if (orphanedIds.length > 0) {
      log.info('[CitationCleanup] Found orphaned citations:', orphanedIds.length);
      
      // Remove orphaned citations from Y.js Map
      orphanedIds.forEach((id: string) => {
        citationsMap.delete(id);
        log.info('[CitationCleanup] Removed orphaned citation:', id);
      });
    }
  } catch (error) {
    log.error('[CitationCleanup] Error during cleanup:', error);
  }
}

export { citationCleanupKey };

